/**
 * Minimal toast system. Used for save confirmations, "sent to MOST for pricing", and global
 * failures (docs/06 §4: "global failures as toast + panel").
 *
 * Announced via `aria-live="polite"` so a screen-reader user learns that the save landed
 * (docs/06 §12: dynamic regions are polite live regions).
 */
import * as React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { cn } from './primitives';

type ToastTone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastContextValue {
  toast: (message: string, tone?: ToastTone) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

const ICONS: Record<ToastTone, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
};

const TONES: Record<ToastTone, string> = {
  success: 'border-emerald-200 bg-card text-emerald-900',
  error: 'border-red-200 bg-card text-red-900',
  info: 'border-border bg-card text-foreground',
};

const ICON_TONES: Record<ToastTone, string> = {
  success: 'text-emerald-600',
  error: 'text-red-600',
  info: 'text-muted-foreground',
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const nextId = React.useRef(0);

  const dismiss = React.useCallback((id: number) => {
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const toast = React.useCallback(
    (message: string, tone: ToastTone = 'info') => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, tone, message }]);
      // Errors linger longer — the user may need to read them twice.
      window.setTimeout(() => dismiss(id), tone === 'error' ? 7000 : 4000);
    },
    [dismiss],
  );

  const value = React.useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
        aria-live="polite"
        aria-atomic="false"
      >
        <AnimatePresence initial={false}>
          {toasts.map((tst) => {
            const Icon = ICONS[tst.tone];
            return (
              <motion.div
                key={tst.id}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className={cn(
                  'pointer-events-auto flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5',
                  TONES[tst.tone],
                )}
              >
                <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', ICON_TONES[tst.tone])} aria-hidden />
                <p className="flex-1 text-sm">{tst.message}</p>
                <button
                  type="button"
                  onClick={() => dismiss(tst.id)}
                  className="rounded p-0.5 text-muted-foreground/70 hover:text-foreground"
                  aria-label="Close"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}
